import { backoffAuthCall } from 'core_main/api/pubnub';
import { updateStore } from 'core_main/store';
import { getConversationPubNub, getMetaPubNub } from './instance';
import { logHealthCheckError } from './logError';

// Type for the health check function to avoid circular dependency
type HealthCheckFunction = () => Promise<void>;

interface GrantTokenResponse {
  token: string;
  ttl: number;
}

// Module-scoped variable to store the injected health check function
let injectedHealthCheck: HealthCheckFunction | null = null;

/**
 * Sets the health check function to be called after token refresh.
 * This uses dependency injection to avoid circular dependencies.
 * Called from index.ts during initialization.
 */
export function setHealthCheckFunction(healthCheck: HealthCheckFunction) {
  injectedHealthCheck = healthCheck;
}

/*
  This helper handles the nitty-gritty of refreshing the Pubnub grant token based on the ttl
*/
let authTokenInterval: NodeJS.Timeout | undefined;
let authTokenExpiration: Date | undefined;
let grantTokenRefreshPromise:
  | Promise<GrantTokenResponse | undefined>
  | undefined;

export function isGrantTokenRefreshInProgress(): boolean {
  return grantTokenRefreshPromise !== undefined;
}

export function computeGrantTokenExpiration(ttl: number) {
  const buffer = ttl / 10;
  return ttl - buffer;
}

export async function refreshGrantToken(
  outboundChatEnabled,
): Promise<GrantTokenResponse | undefined> {
  if (grantTokenRefreshPromise) {
    return grantTokenRefreshPromise;
  }

  grantTokenRefreshPromise = backoffAuthCall({
    version: 2,
    outboundChatEnabled,
  }).then((authResponse) => {
    // return without continuing to initialize the pubnub instance
    // because the auth returned an empty response which means there are no existing conversations
    if (!authResponse) return undefined;
    getMetaPubNub()?.instance?.setToken(authResponse.token);
    getConversationPubNub()?.instance?.setToken(authResponse.token);
    authTokenExpiration = new Date(
      new Date().getTime() + computeGrantTokenExpiration(authResponse.ttl),
    );

    return authResponse;
  });

  let authResponse: GrantTokenResponse | undefined;
  try {
    authResponse = await grantTokenRefreshPromise;
  } finally {
    grantTokenRefreshPromise = undefined;
  }

  // Auth Expires trigger: Check and reconnect after token refresh completes
  // Non-blocking call (fire-and-forget) to avoid delaying token refresh
  // Uses dependency injection to avoid circular dependencies
  if (authResponse && injectedHealthCheck) {
    injectedHealthCheck().catch((error) => {
      logHealthCheckError(error, 'Health check failed after token refresh');
    });
  }

  return authResponse;
}
export function tryToRefreshToken(outboundChatEnabled) {
  // Health check is already called in refreshGrantToken, no need to call again here
  return refreshGrantToken(outboundChatEnabled).catch(() => {
    updateStore('isPubNubConnected', false);
    return undefined; // Explicit return for clarity
  });
}
export function teardownGrantTokenRefresh() {
  if (authTokenInterval) {
    clearInterval(authTokenInterval);
    authTokenInterval = undefined;
  }
}
export function setupGrantTokenRefresh(outboundChatEnabled) {
  return refreshGrantToken(outboundChatEnabled).then((authResponse) => {
    // return without continuing to initialize the pubnub instance
    // because the auth returned an empty response which means there are no existing conversations
    if (!authResponse) return undefined;
    teardownGrantTokenRefresh();
    authTokenInterval = setInterval(
      () => tryToRefreshToken(outboundChatEnabled),
      computeGrantTokenExpiration(authResponse.ttl),
    );
    return authResponse;
  });
}

export function isAuthTokenExpired() {
  return authTokenExpiration !== undefined && new Date() > authTokenExpiration;
}
